/**
 * @file XeroHealthBanner.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-03
 * @description Says so when Xero is not answering. Advisory only.
 *
 *              The server works out one health state for the whole system from
 *              what its own calls to Xero experience (services/xeroHealthService
 *              — ok / degraded / down / auth_problem) and sends it on the status
 *              poll every tab already makes (useSystemStatus). This turns it
 *              into a strip across the top:
 *
 *                down          everyone (except reps, who never use Xero)
 *                auth_problem  everyone: the system's own connection was refused
 *                degraded      admins only — one slow answer is not news to
 *                              the office, but it is to whoever looks after it
 *
 *              Nothing is blocked. Work here saves to our own database whatever
 *              Xero is doing; only the things that must reach Xero there and
 *              then (pushing an invoice, a contact change) can fail, and they
 *              say so themselves. Contrast XeroRequiredGate, which blocks
 *              because a person's own connection is missing — something they
 *              can fix. Nobody in the office can fix Xero being down, so
 *              stopping them would only invent an outage of our own.
 *
 *              Same slot shape as MaintenanceBanner (the app slides down rather
 *              than being covered), and the same classes for the strip.
 */

import React from 'react';
import './MaintenanceBanner.css';
import './XeroHealthBanner.css';

function hhmm(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
}

function messageFor(state, since, custom) {
  const at = hhmm(since);
  const sinceText = at ? ` since ${at}` : '';
  if (state === 'down') {
    return {
      icon: 'cloud_off',
      lead: `Xero is not answering${sinceText}.`,
      rest: 'Work here saves as normal. Anything that has to go to Xero straight away — pushing an invoice, changing a contact — may fail until it is back; try those again later.'
    };
  }
  if (state === 'auth_problem') {
    return {
      icon: 'key_off',
      lead: `The system cannot sign in to Xero${sinceText}.`,
      rest: custom
        ? 'Work here saves as normal; nothing is sent to Xero until the system connection is fixed. Please let an administrator know.'
        : 'Work here saves as normal. Please let an administrator know.'
    };
  }
  return {
    icon: 'hourglass_top',
    lead: `Xero is slow or busy${sinceText}.`,
    rest: 'Some calls to it have failed or timed out; it usually clears by itself.'
  };
}

export default function XeroHealthBanner({ health, isAdmin = false, hidden = false }) {
  const state = health?.state;
  const show = !hidden && !!health && !health.stale && (
    state === 'down' || state === 'auth_problem' || (state === 'degraded' && isAdmin)
  );
  const msg = show ? messageFor(state, health.since, health.mode === 'custom') : null;

  return (
    <div className={`maint-slot${show ? ' open' : ''}`} aria-hidden={!show}>
      <div className="maint-slot-inner">
        {show && (
          <div className={`maint-strip xero-health-strip xero-health-${state}`} role="status" aria-live="polite">
            <span className="material-symbols-rounded">{msg.icon}</span>
            <span className="maint-text">
              <strong>{msg.lead}</strong>{' '}{msg.rest}
              {isAdmin && health.lastError && (
                <span className="xero-health-detail"> Last error: {health.lastError}</span>
              )}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
